import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  borrarMedicion,
  crearMedicion,
  listarMediciones,
  type Medicion,
  TipoMedicion,
} from "../api";
import { formatearFecha } from "../formato";

type Props = {
  mascotaID: string;
  nombreMascota: string;
  puedeEditar: boolean;
};

type PuntoGrafico = Medicion & {
  x: number;
  y: number;
  minVal: number;
  maxVal: number;
};

export default function GraficoPeso({ mascotaID, nombreMascota, puedeEditar }: Props) {
  const qc = useQueryClient();
  const [vista, setVista] = useState<"grafico" | "tabla">("grafico");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [puntoSeleccionado, setPuntoSeleccionado] = useState<Medicion | null>(null);
  const [medicionParaBorrar, setMedicionParaBorrar] = useState<number | null>(null);

  // Formulario rápido de pesaje
  const hoy = new Date().toISOString().slice(0, 10);
  const [nuevoPeso, setNuevoPeso] = useState<string>("");
  const [nuevaFecha, setNuevaFecha] = useState<string>(hoy);
  const [errorForm, setErrorForm] = useState<string | null>(null);

  const medicionesQuery = useQuery({
    queryKey: ["mediciones", mascotaID],
    queryFn: () => listarMediciones(mascotaID),
    enabled: Boolean(mascotaID),
  });

  const mutacionCrear = useMutation({
    mutationFn: (datos: { valor: number; fecha: string }) =>
      crearMedicion({
        mascotaID,
        tipo: TipoMedicion.Peso,
        valor: datos.valor,
        fecha: datos.fecha,
        unidad: "kg",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mediciones", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascota", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      setModalAbierto(false);
      setNuevoPeso("");
      setNuevaFecha(hoy);
      setErrorForm(null);
    },
    onError: (err: Error) => {
      setErrorForm(err.message || "Error al guardar el pesaje.");
    },
  });

  const mutacionBorrar = useMutation({
    mutationFn: (medicionID: number) => borrarMedicion(medicionID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mediciones", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascota", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      setMedicionParaBorrar(null);
      if (puntoSeleccionado && medicionParaBorrar === puntoSeleccionado.medicionID) {
        setPuntoSeleccionado(null);
      }
    },
  });

  const mediciones = useMemo(() => {
    return (medicionesQuery.data ?? []).slice().sort((a, b) => {
      if (a.fecha !== b.fecha) return a.fecha.localeCompare(b.fecha);
      return a.medicionID - b.medicionID;
    });
  }, [medicionesQuery.data]);

  // Estadísticas clave
  const stats = useMemo(() => {
    if (mediciones.length === 0) return null;
    const ultimo = mediciones[mediciones.length - 1];
    if (!ultimo) return null;

    const penultimo = mediciones.length > 1 ? mediciones[mediciones.length - 2] : null;
    const primero = mediciones[0] ?? ultimo;
    const valores = mediciones.map((m) => m.valor);
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const delta = penultimo ? ultimo.valor - penultimo.valor : 0;
    const deltaTotal = ultimo.valor - primero.valor;

    return {
      actual: ultimo.valor,
      fechaActual: ultimo.fecha,
      min,
      max,
      delta,
      deltaTotal,
      cantidad: mediciones.length,
    };
  }, [mediciones]);

  // Dimensiones del SVG
  const W = 620;
  const H = 240;
  const pad = { top: 30, right: 35, bottom: 40, left: 48 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  // Cálculo de coordenadas de los puntos
  const puntosGrafico = useMemo<PuntoGrafico[]>(() => {
    if (mediciones.length === 0) return [];

    const valores = mediciones.map((m) => m.valor);
    let minVal = Math.min(...valores);
    let maxVal = Math.max(...valores);

    if (minVal === maxVal) {
      minVal = Math.max(0, minVal - 2);
      maxVal = maxVal + 2;
    } else {
      const margen = (maxVal - minVal) * 0.15;
      minVal = Math.max(0, Number((minVal - margen).toFixed(1)));
      maxVal = Number((maxVal + margen).toFixed(1));
    }

    const n = mediciones.length;
    return mediciones.map((m, i) => {
      const x = n === 1 ? pad.left + innerW / 2 : pad.left + (i / (n - 1)) * innerW;
      const y = pad.top + innerH - ((m.valor - minVal) / (maxVal - minVal)) * innerH;
      return { ...m, x, y, minVal, maxVal };
    });
  }, [mediciones, pad.left, pad.top, innerW, innerH]);

  // Generador de path SVG suave
  const pathD = useMemo(() => {
    if (puntosGrafico.length < 2) return "";

    const primerPunto = puntosGrafico[0];
    if (!primerPunto) return "";

    let d = `M ${primerPunto.x} ${primerPunto.y}`;
    for (let i = 0; i < puntosGrafico.length - 1; i++) {
      const p0 = puntosGrafico[i === 0 ? 0 : i - 1] ?? primerPunto;
      const p1 = puntosGrafico[i] ?? primerPunto;
      const p2 = puntosGrafico[i + 1] ?? primerPunto;
      const p3 =
        puntosGrafico[i + 2 >= puntosGrafico.length ? puntosGrafico.length - 1 : i + 2] ?? p2;

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  }, [puntosGrafico]);

  // Path cerrado para el gradiente de área
  const areaD = useMemo(() => {
    if (!pathD || puntosGrafico.length < 2) return "";
    const ult = puntosGrafico[puntosGrafico.length - 1];
    const prim = puntosGrafico[0];
    if (!ult || !prim) return "";

    const base = pad.top + innerH;
    return `${pathD} L ${ult.x} ${base} L ${prim.x} ${base} Z`;
  }, [pathD, puntosGrafico, pad.top, innerH]);

  const handleSubmitNuevoPeso = (e: React.FormEvent) => {
    e.preventDefault();
    const v = parseFloat(nuevoPeso);
    if (isNaN(v) || v <= 0 || v > 500) {
      setErrorForm("Ingresa un peso válido entre 0.1 y 500 kg.");
      return;
    }
    if (!nuevaFecha) {
      setErrorForm("Selecciona una fecha.");
      return;
    }
    setErrorForm(null);
    mutacionCrear.mutate({ valor: v, fecha: nuevaFecha });
  };

  const primerPuntoGrafico = puntosGrafico[0];

  return (
    <section className="panel seccion-peso">
      <div className="titulo-fila">
        <div>
          <h2>Evolución del peso</h2>
          <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
            Registro cronológico y control corporal de {nombreMascota}
          </p>
        </div>

        <div className="acciones" style={{ gap: "0.5rem" }}>
          {mediciones.length > 0 && (
            <div className="selector-vista-peso" role="group" aria-label="Cambiar vista">
              <button
                type="button"
                className={`boton-tab ${vista === "grafico" ? "activo" : ""}`}
                onClick={() => setVista("grafico")}
              >
                📈 Gráfico
              </button>
              <button
                type="button"
                className={`boton-tab ${vista === "tabla" ? "activo" : ""}`}
                onClick={() => setVista("tabla")}
              >
                📋 Historial ({mediciones.length})
              </button>
            </div>
          )}

          {puedeEditar && (
            <button
              type="button"
              className="boton-primario boton-chico"
              onClick={() => setModalAbierto(true)}
            >
              + Registrar peso
            </button>
          )}
        </div>
      </div>

      {/* Tarjetas de Métricas Estadísticas */}
      {stats && (
        <div className="grid-metricas-peso">
          <div className="tarjeta-stat">
            <span className="stat-etiqueta">Peso actual</span>
            <div className="stat-cifra-fila">
              <span className="stat-valor">
                {stats.actual.toFixed(1)} <small>kg</small>
              </span>
              {stats.cantidad > 1 && (
                <span
                  className={`stat-badge ${
                    stats.delta > 0
                      ? "stat-badge-subida"
                      : stats.delta < 0
                        ? "stat-badge-bajada"
                        : "stat-badge-neutro"
                  }`}
                  title={`Diferencia respecto al pesaje anterior: ${stats.delta > 0 ? "+" : ""}${stats.delta.toFixed(1)} kg`}
                >
                  {stats.delta > 0 ? "↑ +" : stats.delta < 0 ? "↓ " : "= "}
                  {Math.abs(stats.delta).toFixed(1)} kg
                </span>
              )}
            </div>
            <span className="stat-subtexto">Último control: {formatearFecha(stats.fechaActual)}</span>
          </div>

          <div className="tarjeta-stat">
            <span className="stat-etiqueta">Rango histórico</span>
            <div className="stat-cifra-fila">
              <span className="stat-valor-chico">
                {stats.min.toFixed(1)} - {stats.max.toFixed(1)} <small>kg</small>
              </span>
            </div>
            <span className="stat-subtexto">
              {stats.cantidad === 1 ? "1 medición registrada" : `${stats.cantidad} mediciones`}
            </span>
          </div>

          {stats.cantidad > 1 && (
            <div className="tarjeta-stat">
              <span className="stat-etiqueta">Variación total</span>
              <div className="stat-cifra-fila">
                <span className="stat-valor-chico">
                  {stats.deltaTotal > 0 ? "+" : ""}
                  {stats.deltaTotal.toFixed(1)} <small>kg</small>
                </span>
              </div>
              <span className="stat-subtexto">Desde el primer registro</span>
            </div>
          )}
        </div>
      )}

      {/* Estado de Carga o Vacío */}
      {medicionesQuery.isPending && <p className="muted">Cargando serie de peso…</p>}
      {medicionesQuery.error && <p className="err">{medicionesQuery.error.message}</p>}

      {!medicionesQuery.isPending && mediciones.length === 0 && (
        <div className="vacio" style={{ padding: "2rem 1rem" }}>
          <p className="muted">Aún no hay mediciones de peso registradas para {nombreMascota}.</p>
          {puedeEditar && (
            <button
              type="button"
              className="boton-primario"
              onClick={() => setModalAbierto(true)}
              style={{ marginTop: "0.5rem" }}
            >
              Registrar primer pesaje
            </button>
          )}
        </div>
      )}

      {/* Vista de Gráfico Interactivo */}
      {!medicionesQuery.isPending && mediciones.length > 0 && vista === "grafico" && (
        <div className="grafico-peso-contenedor">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="grafico-peso-svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="gradientePeso" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--acento-500, #c45d3c)" stopOpacity="0.32" />
                <stop offset="100%" stopColor="var(--acento-500, #c45d3c)" stopOpacity="0.01" />
              </linearGradient>
            </defs>

            {/* Líneas guía horizontales (eje Y) */}
            {primerPuntoGrafico && (
              <>
                {[0, 0.5, 1].map((fraccion) => {
                  const y = pad.top + innerH * (1 - fraccion);
                  const min = primerPuntoGrafico.minVal;
                  const max = primerPuntoGrafico.maxVal;
                  const valor = min + (max - min) * fraccion;
                  return (
                    <g key={fraccion} className="grafico-guia-y">
                      <line
                        x1={pad.left}
                        y1={y}
                        x2={W - pad.right}
                        y2={y}
                        stroke="currentColor"
                        strokeDasharray="3 3"
                        className="grafico-linea-guia"
                      />
                      <text
                        x={pad.left - 8}
                        y={y + 4}
                        textAnchor="end"
                        className="grafico-etiqueta-eje"
                      >
                        {valor.toFixed(1)} kg
                      </text>
                    </g>
                  );
                })}
              </>
            )}

            {/* Área bajo la curva */}
            {areaD && <path d={areaD} fill="url(#gradientePeso)" />}

            {/* Línea de la curva */}
            {pathD && (
              <path
                d={pathD}
                fill="none"
                stroke="var(--acento-500, #c45d3c)"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Puntos y etiquetas en el eje X */}
            {puntosGrafico.map((pt, idx) => {
              const seleccionado = puntoSeleccionado?.medicionID === pt.medicionID;
              const mostrarEtiqueta =
                puntosGrafico.length <= 6 ||
                idx === 0 ||
                idx === puntosGrafico.length - 1 ||
                idx % Math.ceil(puntosGrafico.length / 5) === 0;

              return (
                <g key={pt.medicionID} className="grafico-punto-grupo">
                  {mostrarEtiqueta && (
                    <text
                      x={pt.x}
                      y={H - 12}
                      textAnchor="middle"
                      className="grafico-etiqueta-fecha"
                    >
                      {formatearFechaCorta(pt.fecha)}
                    </text>
                  )}

                  {/* Círculo invisible más grande para facilitar touch/click */}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r="14"
                    fill="transparent"
                    style={{ cursor: "pointer" }}
                    onClick={() => setPuntoSeleccionado(seleccionado ? null : pt)}
                  />

                  {/* Círculo visible */}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r={seleccionado ? "6.5" : "4.5"}
                    className={`grafico-punto ${seleccionado ? "activo" : ""} ${
                      pt.esDeVisita ? "punto-visita" : "punto-manual"
                    }`}
                    onClick={() => setPuntoSeleccionado(seleccionado ? null : pt)}
                  />
                </g>
              );
            })}
          </svg>

          {/* Tarjeta de Detalle / Tooltip del Punto Seleccionado */}
          {puntoSeleccionado && (
            <div className="grafico-tooltip-tarjeta">
              <div className="tooltip-cabecera">
                <div>
                  <span className="tooltip-valor">{puntoSeleccionado.valor.toFixed(1)} kg</span>
                  <span className="tooltip-fecha">{formatearFecha(puntoSeleccionado.fecha)}</span>
                </div>
                <span className="etiqueta neutra">
                  {puntoSeleccionado.esDeVisita ? "🩺 Atención en visita" : "🏠 Control en casa"}
                </span>
              </div>
              <div className="tooltip-acciones">
                {puedeEditar && !puntoSeleccionado.esDeVisita && (
                  <button
                    type="button"
                    className="boton-peligro boton-chico"
                    onClick={() => mutacionBorrar.mutate(puntoSeleccionado.medicionID)}
                    disabled={mutacionBorrar.isPending}
                  >
                    Eliminar registro
                  </button>
                )}
                <button
                  type="button"
                  className="boton-chico"
                  onClick={() => setPuntoSeleccionado(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista de Tabla Histórica */}
      {!medicionesQuery.isPending && mediciones.length > 0 && vista === "tabla" && (
        <div className="tabla-contenedor">
          <table className="tabla-peso">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Peso</th>
                <th>Origen</th>
                <th>Diferencia</th>
                {puedeEditar && <th style={{ textAlign: "right" }}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {mediciones
                .slice()
                .reverse()
                .map((med, idx, arr) => {
                  const previo = idx < arr.length - 1 ? arr[idx + 1] : null;
                  const delta = previo ? med.valor - previo.valor : null;

                  return (
                    <tr key={med.medicionID}>
                      <td>
                        <strong>{formatearFecha(med.fecha)}</strong>
                      </td>
                      <td>
                        <span className="peso-cifra">{med.valor.toFixed(1)} kg</span>
                      </td>
                      <td>
                        {med.esDeVisita ? (
                          <span
                            className="etiqueta neutra"
                            title="Registrado durante una visita clínica"
                          >
                            🩺 Visita clínica
                          </span>
                        ) : (
                          <span className="etiqueta neutra" title="Registrado directamente">
                            🏠 Control en casa
                          </span>
                        )}
                      </td>
                      <td>
                        {delta !== null ? (
                          <span
                            className={`delta-tag ${
                              delta > 0
                                ? "delta-subida"
                                : delta < 0
                                  ? "delta-bajada"
                                  : "delta-neutro"
                            }`}
                          >
                            {delta > 0 ? "↑ +" : delta < 0 ? "↓ " : "= "}
                            {Math.abs(delta).toFixed(1)} kg
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      {puedeEditar && (
                        <td style={{ textAlign: "right" }}>
                          {medicionParaBorrar === med.medicionID ? (
                            <div className="acciones-borrar-inline">
                              <button
                                type="button"
                                className="boton-peligro boton-chico"
                                onClick={() => mutacionBorrar.mutate(med.medicionID)}
                                disabled={mutacionBorrar.isPending}
                              >
                                Confirmar
                              </button>
                              <button
                                type="button"
                                className="boton-chico"
                                onClick={() => setMedicionParaBorrar(null)}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="boton-chico boton-borrar-icono"
                              title="Eliminar pesaje"
                              onClick={() => setMedicionParaBorrar(med.medicionID)}
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Registro Rápido de Peso */}
      {modalAbierto && (
        <div className="modal-backdrop" onClick={() => setModalAbierto(false)}>
          <div
            className="modal-dialog modal-peso"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-cabecera">
              <h3 style={{ margin: 0 }}>Registrar peso de {nombreMascota}</h3>
              <button
                type="button"
                className="modal-cerrar-btn"
                onClick={() => setModalAbierto(false)}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitNuevoPeso} className="formulario-peso">
              {errorForm && <p className="err">{errorForm}</p>}

              <div className="campo">
                <label htmlFor="peso-input">
                  <strong>Peso (kg) *</strong>
                </label>
                <input
                  id="peso-input"
                  type="number"
                  step="0.05"
                  min="0.1"
                  max="500"
                  placeholder="ej. 14.5"
                  value={nuevoPeso}
                  onChange={(e) => setNuevoPeso(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              <div className="campo">
                <label htmlFor="fecha-input">
                  <strong>Fecha de pesaje *</strong>
                </label>
                <input
                  id="fecha-input"
                  type="date"
                  max={hoy}
                  value={nuevaFecha}
                  onChange={(e) => setNuevaFecha(e.target.value)}
                  required
                />
              </div>

              <div className="acciones-modal">
                <button
                  type="button"
                  className="boton"
                  onClick={() => setModalAbierto(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="boton-primario"
                  disabled={mutacionCrear.isPending || !nuevoPeso || !nuevaFecha}
                >
                  {mutacionCrear.isPending ? "Guardando…" : "Guardar peso"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function formatearFechaCorta(fechaStr: string): string {
  try {
    const partes = fechaStr.split("-");
    const m = Number(partes[1]);
    const d = Number(partes[2]);
    const meses = [
      "ene",
      "feb",
      "mar",
      "abr",
      "may",
      "jun",
      "jul",
      "ago",
      "sep",
      "oct",
      "nov",
      "dic",
    ];
    const nombreMes = meses[m - 1] ?? "";
    return `${d} ${nombreMes}`;
  } catch {
    return fechaStr;
  }
}
